import { ReadonlyQuat, ReadonlyVec3, vec3 } from "gl-matrix";

import { degToRad } from "utils/math";
import { isRealVec } from "utils/misc";

export function getSurfaceNormal(normal: ReadonlyVec3 | undefined): vec3 | undefined {
    if (!normal) {
        return undefined;
    }

    const candidate = vec3.fromValues(normal[0] ?? NaN, normal[1] ?? NaN, normal[2] ?? NaN);
    if (!isRealVec([candidate[0], candidate[1], candidate[2]]) || vec3.dot(candidate, candidate) === 0) {
        return undefined;
    }

    return vec3.normalize(candidate, candidate);
}

/**
 * Flips `normal` (in place) so it points back towards the camera that picked `point`, i.e. out of the
 * side of the surface that was seen. Pick normals aren't guaranteed to face the viewer (e.g. back faces
 * of open meshes), so this pins down which side was clicked while the picking camera is still known.
 */
export function orientNormalTowardsCamera(normal: vec3, point: ReadonlyVec3, camera: PickingCamera): vec3 {
    if (vec3.dot(normal, getDirectionTowardsCamera(point, camera)) < 0) {
        vec3.negate(normal, normal);
    }
    return normal;
}

type PickingCamera = { kind: string; position: ReadonlyVec3; rotation: ReadonlyQuat };

/**
 * Direction (not normalized) from `point` back towards the camera. Pinhole: towards the eye. Ortho: every
 * ray is parallel to the view direction, so against it, wherever the camera position happens to be.
 */
function getDirectionTowardsCamera(point: ReadonlyVec3, camera: PickingCamera): vec3 {
    return camera.kind === "orthographic"
        ? vec3.transformQuat(vec3.create(), vec3.fromValues(0, 0, 1), camera.rotation)
        : vec3.subtract(vec3.create(), camera.position, point);
}

export function getPlaneCreationNormal(normal: ReadonlyVec3 | undefined): vec3 {
    return getSurfaceNormal(normal) ?? vec3.fromValues(0, 0, 1);
}

/** Cuts within this angle of perfectly horizontal/vertical snap to exact 0°/90°. */
export const AXIS_SNAP_TOLERANCE_DEG = 0.5;

const cosTolerance = Math.cos(degToRad(AXIS_SNAP_TOLERANCE_DEG)); // near-vertical normal
const sinTolerance = Math.sin(degToRad(AXIS_SNAP_TOLERANCE_DEG)); // near-horizontal normal

export function snapSectionPlaneNormalToAxis(normal: vec3): vec3 {
    const nz = normal[2];
    const absNz = Math.abs(nz);
    if (absNz >= cosTolerance) {
        // normal ~parallel to Z → perfectly horizontal cut
        return vec3.fromValues(0, 0, nz < 0 ? -1 : 1);
    }
    if (absNz <= sinTolerance) {
        // normal ~perpendicular to Z → perfectly vertical cut; keep azimuth, drop tilt
        const snapped = vec3.fromValues(normal[0], normal[1], 0);
        return vec3.normalize(snapped, snapped);
    }
    return normal;
}
